/**
 * Heavy Equipment (Gold) — the client-side half, issue #362.
 *
 * The frontend holds no equipment data. Power ratings, duty factors, the
 * electricity tariff and the emissions factor are resolved server-side and
 * never reach the client; this file is display copy plus the field-visibility
 * rules, which the manifest cannot express.
 *
 * An add-row assessment: one row per piece of equipment, the type picked from a
 * dropdown, the same shape as Procurement and Eco Office. Unlike those two the
 * rows are heterogeneous: 55 columns are served and a row uses 5 to 10 of them,
 * chosen by its equipment type.
 */

/**
 * `CalcCategories.HEAVY_EQUIPMENT` is `"Heavy Equipment"` and
 * `catalog_metadata()` agrees, but `tierConfig` still labels the gold card
 * "Equipment". Match both until the naming settles.
 */
export const HEAVY_EQUIPMENT_CATEGORIES = [
  "Heavy Equipment",
  "Equipment",
] as const;

export const HEAVY_EQUIPMENT_CATEGORY = HEAVY_EQUIPMENT_CATEGORIES[0];

export const isHeavyEquipmentCategory = (category: string | null): boolean =>
  category !== null &&
  (HEAVY_EQUIPMENT_CATEGORIES as readonly string[]).includes(category);

/**
 * Field key by served column id, straight from the manifest's
 * `legacy_column_id`.
 *
 * The rules below are keyed by field key, not label: "BSC Width" carries its
 * prefix but "Typical Sash Position During Use" and "Additional Blower Runtime"
 * do not, so labels cannot identify which equipment a column belongs to. The
 * ids are contract, the same way `ECO_OFFICE_COLUMN_IDS` is, and
 * `heavyEquipmentCatalogue.test.ts` holds this table against the recorded
 * fixture so a reseed that renumbered anything fails there.
 */
export const HEAVY_EQUIPMENT_FIELD_KEY_BY_COLUMN_ID: Record<number, string> = {
  1: "equipment_type",
  2: "quantity_units",
  3: "quantity_team_share",
  4: "days_used_per_week",
  5: "fume_use_minutes_per_day",
  6: "fume_sash_position_in_use",
  7: "fume_sash_position_post_use",
  8: "bsc_width",
  9: "bsc_active_use_minutes_per_day",
  10: "bsc_additional_blower_runtime_minutes_per_day",
  11: "bsc_uv_runtime_minutes_per_day",
  12: "bsc_left_running_on_unused_days",
  13: "bsc_uv_left_on_unused_days",
  14: "incubator_size",
  15: "incubator_operating_temperature",
  16: "incubator_door_openings_per_used_day",
  17: "incubator_door_open_duration",
  18: "incubator_empty_days_per_year",
  19: "autoclave_type",
  21: "autoclave_cycles_per_week",
  22: "autoclave_typical_load",
  23: "autoclave_powered_between_cycles",
  24: "autoclave_powered_overnight",
  25: "centrifuge_type",
  26: "centrifuge_operating_temperature",
  27: "centrifuge_active_minutes_per_day",
  28: "centrifuge_turned_off_end_of_day",
  29: "centrifuge_refrigeration_left_on_overnight",
  30: "pcr_runs_per_day",
  31: "pcr_overnight_state",
  32: "pcr_overnight_hold_required",
  33: "water_bath_size",
  34: "water_bath_medium",
  35: "water_bath_operating_temperature",
  36: "water_bath_lid_used",
  37: "water_bath_required_hours_on_per_day",
  38: "water_bath_left_on_overnight_not_required",
  39: "water_bath_left_on_unused_days",
  40: "thermomixer_operating_temperature",
  41: "thermomixer_active_hours_per_day",
  42: "thermomixer_left_maintaining_temperature_after_use",
  43: "thermomixer_additional_hours_left_on_per_day",
  44: "dry_block_size",
  45: "dry_block_operating_temperature",
  46: "dry_block_active_hours_per_day",
  47: "dry_block_left_maintaining_temperature_after_use",
  48: "dry_block_additional_hours_left_on_per_day",
  49: "hotplate_mode",
  50: "hotplate_heating_level",
  51: "hotplate_active_hours_per_day",
  52: "hotplate_left_heating_after_use",
  53: "hotplate_additional_heating_hours_per_day",
  // PCR's three later additions sit past the hotplate block because ids follow
  // the order Alex added them, not display order.
  54: "pcr_configuration",
  55: "pcr_overnight_hours",
  56: "pcr_overnight_nights_per_week",
};

export const fieldKeyOf = (columnId: number | string): string =>
  HEAVY_EQUIPMENT_FIELD_KEY_BY_COLUMN_ID[Number(columnId)] ?? "";

const COLUMN_ID_BY_FIELD_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(HEAVY_EQUIPMENT_FIELD_KEY_BY_COLUMN_ID).map(([id, key]) => [
    key,
    id,
  ]),
);

export const columnIdOf = (fieldKey: string): string =>
  COLUMN_ID_BY_FIELD_KEY[fieldKey] ?? "";

/** The column that decides what the rest of the row asks. */
export const EQUIPMENT_TYPE_FIELD_KEY = "equipment_type";

/** The four fields no prefix claims. Two of them are alternatives, see below. */
export const SHARED_FIELD_KEYS = [
  EQUIPMENT_TYPE_FIELD_KEY,
  "quantity_units",
  "quantity_team_share",
  "days_used_per_week",
] as const;

/**
 * The ten equipment types and the shape of each one's row.
 *
 * `prefix` claims all 49 per-equipment fields: every field key is either one of
 * the four shared ones or begins with exactly one of these. That is the whole
 * rule, so a type costs three lines here instead of a hand-kept list of its
 * eight columns.
 *
 * The two quantity fields are alternatives, not both: a fume hood is counted in
 * whole units, an incubator as the share of one the team uses. Autoclaves are
 * the one type with no `days_used_per_week`, since cycles per week covers it.
 */
const EQUIPMENT_SHAPES = {
  "Fume hood - Ducted VAV": {
    prefix: "fume_",
    quantityFieldKey: "quantity_units",
    usesDaysPerWeek: true,
  },
  "Biosafety cabinet - Class II A2": {
    prefix: "bsc_",
    quantityFieldKey: "quantity_units",
    usesDaysPerWeek: true,
  },
  Incubator: {
    prefix: "incubator_",
    quantityFieldKey: "quantity_team_share",
    usesDaysPerWeek: true,
  },
  Autoclave: {
    prefix: "autoclave_",
    quantityFieldKey: "quantity_units",
    usesDaysPerWeek: false,
  },
  Centrifuge: {
    prefix: "centrifuge_",
    quantityFieldKey: "quantity_team_share",
    usesDaysPerWeek: true,
  },
  "PCR thermal cycler": {
    prefix: "pcr_",
    quantityFieldKey: "quantity_team_share",
    usesDaysPerWeek: true,
  },
  "Water / bead bath": {
    prefix: "water_bath_",
    quantityFieldKey: "quantity_team_share",
    usesDaysPerWeek: true,
  },
  Thermomixer: {
    prefix: "thermomixer_",
    quantityFieldKey: "quantity_team_share",
    usesDaysPerWeek: true,
  },
  "Dry block heater": {
    prefix: "dry_block_",
    quantityFieldKey: "quantity_team_share",
    usesDaysPerWeek: true,
  },
  "Hotplate / hotplate stirrer": {
    prefix: "hotplate_",
    quantityFieldKey: "quantity_team_share",
    usesDaysPerWeek: true,
  },
} as const satisfies Record<
  string,
  { prefix: string; quantityFieldKey: string; usesDaysPerWeek: boolean }
>;

export type EquipmentType = keyof typeof EQUIPMENT_SHAPES;

/** The type options in served order, so the test can hold them against the
 * schema's `allowed_values`. */
export const EQUIPMENT_TYPES = Object.keys(
  EQUIPMENT_SHAPES,
) as readonly EquipmentType[];

export const EQUIPMENT_PREFIXES = Object.values(EQUIPMENT_SHAPES).map(
  (shape) => shape.prefix,
);

export const isEquipmentType = (value: string): value is EquipmentType =>
  Object.hasOwn(EQUIPMENT_SHAPES, value);

/**
 * Follow-up questions that stop applying once their trigger is answered.
 *
 * These mirror `AssessmentValueService._handle_heavy_equipment_change`, which
 * soft-deletes the same keys on the same answers, and the per-type calculators,
 * which stop requiring them under the same condition. One list serves both jobs
 * here: hiding the question, and knowing a row is still short of a figure.
 */
const FOLLOW_UP_RULES: readonly {
  whenFieldKey: string;
  equals: readonly string[];
  hideFieldKeys: readonly string[];
}[] = [
  {
    whenFieldKey: "centrifuge_type",
    equals: ["Benchtop non-refrigerated"],
    hideFieldKeys: ["centrifuge_refrigeration_left_on_overnight"],
  },
  {
    // All three, matching the backend: `value_service` soft-deletes the hold
    // flag, the hours and the nights together once the state is OFF.
    whenFieldKey: "pcr_overnight_state",
    equals: ["OFF"],
    hideFieldKeys: [
      "pcr_overnight_hold_required",
      "pcr_overnight_hours",
      "pcr_overnight_nights_per_week",
    ],
  },
  {
    whenFieldKey: "thermomixer_left_maintaining_temperature_after_use",
    equals: ["No"],
    hideFieldKeys: ["thermomixer_additional_hours_left_on_per_day"],
  },
  {
    whenFieldKey: "dry_block_left_maintaining_temperature_after_use",
    equals: ["No"],
    hideFieldKeys: ["dry_block_additional_hours_left_on_per_day"],
  },
  {
    whenFieldKey: "hotplate_mode",
    equals: ["Stir only"],
    hideFieldKeys: [
      "hotplate_heating_level",
      "hotplate_left_heating_after_use",
      "hotplate_additional_heating_hours_per_day",
    ],
  },
  {
    whenFieldKey: "hotplate_left_heating_after_use",
    equals: ["No"],
    hideFieldKeys: ["hotplate_additional_heating_hours_per_day"],
  },
];

/** A row's answers by field key. */
export type EquipmentRowValues = Record<string, string>;

/**
 * Rekey a row's answers from column id to field key, which is what the rules
 * below are written in. Form state is keyed by column id throughout.
 */
export const rowValuesByFieldKey = (
  values: Record<string, string>,
): EquipmentRowValues => {
  const rekeyed: EquipmentRowValues = {};
  for (const [columnId, value] of Object.entries(values)) {
    const fieldKey = fieldKeyOf(columnId);
    if (fieldKey) rekeyed[fieldKey] = value;
  }
  return rekeyed;
};

const hasValue = (value: string | undefined): boolean =>
  value !== undefined && value.trim() !== "";

/**
 * The field keys this row should be asking about, in served order.
 *
 * Empty until the type is picked, bar the type itself, which is why the first
 * column stays editable while everything else waits on it.
 */
export const visibleFieldKeysFor = (
  equipmentType: string,
  values: EquipmentRowValues,
): Set<string> => {
  const visible = new Set<string>([EQUIPMENT_TYPE_FIELD_KEY]);
  if (!isEquipmentType(equipmentType)) return visible;

  const shape = EQUIPMENT_SHAPES[equipmentType];
  visible.add(shape.quantityFieldKey);
  if (shape.usesDaysPerWeek) visible.add("days_used_per_week");

  for (const fieldKey of Object.values(
    HEAVY_EQUIPMENT_FIELD_KEY_BY_COLUMN_ID,
  )) {
    if (fieldKey.startsWith(shape.prefix)) visible.add(fieldKey);
  }

  for (const rule of FOLLOW_UP_RULES) {
    if (!visible.has(rule.whenFieldKey)) continue;
    if (!rule.equals.includes(values[rule.whenFieldKey] ?? "")) continue;
    for (const hidden of rule.hideFieldKeys) visible.delete(hidden);
  }

  return visible;
};

/**
 * Field keys this answer just stopped applying to, so their values can be
 * cleared alongside it.
 *
 * `AssessmentValueService` soft-deletes exactly these server-side. Local state
 * has to follow or the two diverge: a field that reappears later still shows
 * its old answer while the server holds nothing, `missingFieldKeysFor` sees a
 * filled row, and submit is rejected naming neither row nor field.
 */
export const droppedFieldKeysFor = (
  before: EquipmentRowValues,
  after: EquipmentRowValues,
): string[] => {
  const visibleBefore = visibleFieldKeysFor(
    before[EQUIPMENT_TYPE_FIELD_KEY] ?? "",
    before,
  );
  const visibleAfter = visibleFieldKeysFor(
    after[EQUIPMENT_TYPE_FIELD_KEY] ?? "",
    after,
  );
  return [...visibleBefore].filter((fieldKey) => !visibleAfter.has(fieldKey));
};

/**
 * Visible questions this row has not answered yet.
 *
 * `equipment_type` is the only field the schema marks required, and Heavy
 * Equipment defines no `is_complete`, so nothing server-side flags a short row
 * until submit. The calculator needs every visible answer, and one missing
 * figure rejects the whole submission with "Fill all required fields" naming
 * neither the row nor the field. Hence this.
 */
export const missingFieldKeysFor = (values: EquipmentRowValues): string[] => {
  const equipmentType = values[EQUIPMENT_TYPE_FIELD_KEY] ?? "";
  if (!hasValue(equipmentType)) return [];

  return [...visibleFieldKeysFor(equipmentType, values)].filter(
    (fieldKey) => !hasValue(values[fieldKey]),
  );
};

/**
 * Help text for the questions whose wording does not carry itself. The schema
 * sends every column with a null description, and 55 invented tooltips would be
 * noise, so the self-explanatory ones get none.
 */
export const HEAVY_EQUIPMENT_TOOLTIPS_BY_FIELD_KEY: Record<string, string> = {
  equipment_type:
    "The piece of equipment this row is about. Add a row for each one, and list the same type twice if the answers differ.",
  quantity_units: "How many of these your lab has, from 1 to 30.",
  quantity_team_share:
    "Your team's share of the unit, so 0.5 for one shared with another team. Use whole numbers if your team has them to itself.",
  days_used_per_week:
    "Days a week the equipment is actually used, to the nearest half day.",
  fume_sash_position_post_use:
    "Where the sash is usually left once the work is finished, which is often not where it sat during use.",
  bsc_additional_blower_runtime_minutes_per_day:
    "Blower time beyond the active use above, such as a purge before or after work.",
  bsc_uv_runtime_minutes_per_day:
    "Daily UV lamp time. Enter 0 if the lamp is never run.",
  incubator_empty_days_per_year:
    "Days a year it runs with nothing in it, over shutdowns and between experiments.",
  autoclave_cycles_per_week:
    "Cycles a week across the whole year. There is no separate days-per-week question for autoclaves.",
  autoclave_typical_load: "How full the chamber usually is on a cycle.",
  autoclave_powered_between_cycles:
    "Whether it is left powered and up to temperature between cycles during the day.",
  centrifuge_operating_temperature:
    "The set temperature. Still worth answering for a non-refrigerated unit, where it records the ambient the rotor runs at.",
  centrifuge_turned_off_end_of_day:
    "Whether the unit is switched off, not just left idle, at the end of the day.",
  pcr_configuration:
    "The block layout of the machine. Pick Standard 96 if you are not sure.",
  pcr_runs_per_day:
    "Runs on a day it is used, not hours. Quarter runs are allowed for a machine you share.",
  pcr_overnight_hours:
    "Hours a night it holds temperature. Only asked when the overnight state is not OFF.",
  pcr_overnight_nights_per_week:
    "Nights a week it is left holding. Only asked when the overnight state is not OFF.",
  pcr_overnight_state:
    "What the block holds at overnight. Pick OFF if the machine is switched off.",
  pcr_overnight_hold_required:
    "Whether the samples genuinely need that hold, or it is habit.",
  water_bath_required_hours_on_per_day:
    "Hours it needs to be at temperature for the work, not hours it is switched on.",
  water_bath_left_on_overnight_not_required:
    "Yes if it stays on overnight even on days the work does not need it.",
  hotplate_mode: "Pick Stir only if the heater is never used.",
  hotplate_heating_level: "Roughly where the dial sits when the plate is on.",
  hotplate_active_hours_per_day:
    "Hours a day it is actually in use, not hours it is switched on.",
  hotplate_left_heating_after_use:
    "Whether the plate is left hot once the work is finished.",
  thermomixer_left_maintaining_temperature_after_use:
    "Whether the thermomixer is left holding its temperature once the samples are out.",
  thermomixer_additional_hours_left_on_per_day:
    "Hours a day it holds temperature after the work is done, on top of the active hours above.",
  dry_block_left_maintaining_temperature_after_use:
    "Whether the dry block is left holding its temperature once the samples are out.",
  dry_block_additional_hours_left_on_per_day:
    "Hours a day it holds temperature after the work is done, on top of the active hours above.",
  hotplate_additional_heating_hours_per_day:
    "Hours a day the plate stays hot after the work is done, on top of the active hours above.",
};

/**
 * Three different kinds of equipment before submit unlocks. Maz, on #362:
 * "Yes 3 categories not 3 rows is correct".
 *
 * So four centrifuges and nothing else is short; a centrifuge, an incubator and
 * an autoclave is enough.
 */
export const MIN_EQUIPMENT_TYPES = 3;

/**
 * The distinct equipment types a set of rows covers.
 *
 * Rows arrive keyed by column id, as form state holds them, and are rekeyed
 * here so the rule reads the type by field key rather than by position.
 */
export const equipmentTypesCovered = (
  rows: Record<string, string>[],
): Set<string> => {
  const types = new Set<string>();
  for (const values of rows) {
    const type = rowValuesByFieldKey(values)[EQUIPMENT_TYPE_FIELD_KEY] ?? "";
    if (type.trim() !== "") types.add(type.trim());
  }
  return types;
};

/** Whether the rows cover enough different kinds of equipment to submit. */
export const hasEnoughEquipmentTypes = (
  rows: Record<string, string>[],
): boolean => equipmentTypesCovered(rows).size >= MIN_EQUIPMENT_TYPES;

/**
 * What each row is called on screen: its type, numbered once there are two of
 * them, so "Centrifuge 2" in the banner is the second row under Centrifuge.
 * Rows with no type yet keep their fallback label.
 */
export const equipmentRowNames = (
  rows: { id: string; label: string; values: Record<string, string> }[],
): Map<string, string> => {
  const typeOf = (values: Record<string, string>): string =>
    (rowValuesByFieldKey(values)[EQUIPMENT_TYPE_FIELD_KEY] ?? "").trim();
  const totals = new Map<string, number>();
  for (const row of rows) {
    const type = typeOf(row.values);
    totals.set(type, (totals.get(type) ?? 0) + 1);
  }

  const seen = new Map<string, number>();
  const names = new Map<string, string>();
  for (const row of rows) {
    const type = typeOf(row.values);
    const nth = (seen.get(type) ?? 0) + 1;
    seen.set(type, nth);
    names.set(
      row.id,
      !type ? row.label : totals.get(type) === 1 ? type : `${type} ${nth}`,
    );
  }
  return names;
};
